import { generateFormErrorsModel } from 'src/utils/utils'

const api = useFeathers()

export default class {
  constructor(service) {
    this.state = {
      service: service,
      findLoading: false,
      getLoading: false,
      createLoading: false,
      patchLoading: false,
      removeLoading: false,
      item: null,
      validationErrors: {},
      defaultSchema: {},
    }
    this.actions = {
      clearValidationErrors() {
        this.validationErrors = {}
      },
      createFormInstance(onlyFields = null) {
        if (Array.isArray(onlyFields)) {
          const schema = structuredClone(toRaw(this.defaultSchema))
          const filteredSchema = {}
          for (const field of onlyFields) {
            if (schema.hasOwnProperty(field)) {
              filteredSchema[field] = schema[field]
            }
          }
          return filteredSchema
        } else {
          return structuredClone(toRaw(this.defaultSchema))
        }
      },
      onFormDataUpdated(formData, formSchema) {
        // Placeholder function, could be overwritten in the specific service store
      },
      serializeRequestBody(data, schema) {
        let obj = {}
        const keysInSchema = Object.keys(schema)
        for (const key of keysInSchema) {
          // If the data does not contain the key in the schema, set it to null
          if (typeof data?.[key] === 'undefined') {
            obj[key] = null
            continue
          }

          const value = data[key]

          if (value === '__null__') {
            obj[key] = null
            continue
          }

          if (schema[key].type === 'object') {
            const childObject = this.serializeRequestBody(data[key], schema[key].value)
            const isEmpty = Object.values(childObject).every((x) => x === null || x === '' || x === '__null__')
            if (isEmpty) {
              obj[key] = null
            } else {
              obj[key] = childObject
            }
          } else if (schema[key].type === 'array-objects') {
            obj[key] = []
            if (data[key]) {
              for (const entry of data[key]) {
                const childObject = this.serializeRequestBody(entry, schema[key].value)
                const isEmpty = Object.values(childObject).every((x) => x === null || x === '' || x === '__null__')

                if (isEmpty) {
                  obj[key] = schema[key].type === 'boolean' || schema[key].type === 'boolean-small' ? value : null
                } else {
                  obj[key].push(childObject)
                }
              }
            }
          } else if (schema[key].type === 'number' || schema[key].type === 'custom-number') {
            obj[key] = value === null ? null : Number(value)
          } else if (schema[key].type === 'boolean' || schema[key].type === 'boolean-small') {
            obj[key] = value
          } else {
            obj[key] = value === 'undefined' || value === '' ? null : value
          }
        }
        return obj
      },
      clearItem: () => {
        this.item = null
      },
      setItem: (item) => {
        this.item = item
      },
      async find(filter = {}) {
        let items = []
        this.findLoading = true
        if (this.serviceQueryOptions && Object.keys(this.serviceQueryOptions).length > 0) {
          Object.keys(this.serviceQueryOptions).forEach((key) => {
            filter[key] = filter[key] ? filter[key] : this.serviceQueryOptions[key]
          })
        }
        try {
          const response = await api.service(this.service).find({
            query: filter,
          })
          items = typeof response.total !== 'undefined' ? response.data : response
        } catch (e) {
          console.error(e)
          this.findLoading = false
          throw e
        }
        this.findLoading = false
        return items
      },
      async patch(id, item = null, filter = {}) {
        this.validationErrors = {}
        if (!id) {
          if (item?._id) {
            id = item._id
          } else {
            throw new Error('Unable to patch! No ' + (!id ? 'ID' : 'item') + ' provided!')
          }
        }
        if (this.serviceQueryOptions && Object.keys(this.serviceQueryOptions)?.length > 0) {
          Object.keys(this.serviceQueryOptions).forEach((key) => {
            filter[key] = filter[key] ? filter[key] : this.serviceQueryOptions[key]
          })
        }
        if (this.removeDataFromRequests?.length > 0) {
          this.removeDataFromRequests.forEach((key) => {
            if (key && key !== '') {
              delete item[key]
            }
          })
        }

        this.patchLoading = true
        let responseData
        try {
          responseData = await api.service(this.service).patch(id, item, { query: filter })
          if (this.item?._id === responseData._id) {
            this.item = responseData
          }
        } catch (e) {
          this.validationErrors = generateFormErrorsModel(e, this.service)
          this.patchLoading = false
          throw e
        }
        this.patchLoading = false
        return responseData
      },
      async create(item, stripFields = []) {
        this.validationErrors = {}

        this.createLoading = true
        let responseData
        try {
          responseData = await api.service(this.service).create(item)
          const responseDataFiltered = structuredClone(toRaw(responseData))
          for (const stripField of stripFields) {
            delete responseDataFiltered[stripField]
          }
          this.item = responseData
        } catch (e) {
          this.createLoading = false
          this.validationErrors = generateFormErrorsModel(e, this.service)
          throw e
        }
        this.createLoading = false
        return responseData
      },
      async remove(id, params = {}) {
        this.validationErrors = {}
        if (typeof id === 'object' && id._id) {
          id = id._id
        }

        this.removeLoading = true
        let responseData
        try {
          responseData = await api.service(this.service).remove(id, params)
        } catch (e) {
          this.removeLoading = false
          this.validationErrors = generateFormErrorsModel(e, this.service)
          throw e
        }
        this.removeLoading = false
        return responseData
      },
      async get(id, filter = {}, setItemState = true) {
        this.validationErrors = {}
        this.getLoading = true
        if (typeof id === 'object' && id._id) {
          id = id._id
        }

        if (this.serviceQueryOptions && Object.keys(this.serviceQueryOptions)?.length > 0) {
          Object.keys(this.serviceQueryOptions).forEach((key) => {
            filter[key] = filter[key] ? filter[key] : this.serviceQueryOptions[key]
          })
        }

        try {
          const item = await api.service(this.service).get(id, { query: filter })
          if (setItemState) {
            this.item = item
          }
          this.getLoading = false
          return item
        } catch (e) {
          console.error(e)
          this.getLoading = false
          this.validationErrors = generateFormErrorsModel(e, this.service)
          return false
        }
      },
    }
  }
}
